(ns arthur.demo.swarm
  "A hundred and twenty shapes, orbiting, spinning, pulsing and blinking.

  Not useful. It is here because it is the first thing to exercise the DENSE
  channel path end to end — typed-array blocks behind a store handle, one value
  per frame, read through a cursor — which until now had tests and no traffic.
  Step 5 writes exactly this shape out of the freeze module, so it is worth
  knowing the resolver can carry it at rate before anything depends on that.

  Everything is generated from deterministic trigonometry rather than from a
  random seed: the same scene every load, so a stutter or a wrong pose is
  reproducible instead of being a thing that happened once.

  Layout of each block is the rectangular one freeze produces — node-major,
  frame-minor, no per-frame header and no indirection:

      offset(node i) = i · frames · stride
      value(i, f)    = data[offset(i) + f · stride]"
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.palette :as pal]))

(def frames 229)
(def fps 30)
(def n-orbits 6)
(def n-shapes 120)

(def ^:private TAU (* 2 js/Math.PI))

;; Every tone except the background, so the swarm uses the whole ramp.
(def ^:private tones
  (vec (remove #{:bg} (map :name pal/entries))))

(defn- regular-poly
  "A closed n-gon about the origin, flat in [x0 y0 x1 y1 …] — the same layout a
  dense block holds, which is the point of geometry being flat everywhere."
  [n radius phase]
  (vec (mapcat (fn [k]
                 (let [a (+ phase (/ (* TAU k) n))]
                   [(* radius (js/Math.cos a))
                    (* radius (js/Math.sin a))]))
               (range n))))

;; ---------------------------------------------------------------------------
;; the dense blocks

(defn- fill-block!
  "Write one node's frames into a node-major block."
  [^js data i stride f->vals]
  (let [base (* i frames stride)]
    (dotimes [f frames]
      (let [vs (f->vals f)
            o  (+ base (* f stride))]
        (dotimes [k stride]
          (aset data (+ o k) (nth vs k)))))))

(defn- orbit-blocks []
  (let [pos (js/Float32Array. (* n-orbits frames 2))
        rot (js/Float32Array. (* n-orbits frames 1))]
    (dotimes [i n-orbits]
      (let [ph (/ (* TAU i) n-orbits)
            ;; Lissajous, so the six orbits drift in and out of phase with each
            ;; other instead of marching in step.
            wx (+ 0.011 (* 0.004 (mod i 3)))
            wy (+ 0.017 (* 0.003 (mod i 4)))
            spin (* 0.008 (if (even? i) 1 -1) (inc (mod i 3)))]
        (fill-block! pos i 2
                     (fn [f] [(+ 160 (* 104 (js/Math.sin (+ (* f wx) ph))))
                              (+ 100 (* 64 (js/Math.sin (+ (* f wy) (* 1.7 ph)))))]))
        (fill-block! rot i 1 (fn [f] [(* f spin)]))))
    {"swarm/orbit-pos" {:data pos :state nil}
     "swarm/orbit-rot" {:data rot :state nil}}))

(defn- shape-blocks []
  (let [pos   (js/Float32Array. (* n-shapes frames 2))
        rot   (js/Float32Array. (* n-shapes frames 1))
        scale (js/Float32Array. (* n-shapes frames 2))
        ;; The state mask: a handful of shapes wink out entirely for a stretch.
        ;; ABSENT, not hidden — this is the mask meaning "there is no value on
        ;; this frame", which is what an occluded subject will mean at step 6.
        state (js/Uint8Array. (* n-shapes frames))]
    (dotimes [i n-shapes]
      (let [ph    (/ (* TAU i) n-shapes)
            ring  (+ 18 (* 26 (js/Math.abs (js/Math.sin (* 2.3 ph)))))
            wob   (+ 0.03 (* 0.02 (mod i 5)))
            spin  (* (if (zero? (mod i 3)) -1 1) (+ 0.02 (* 0.011 (mod i 7))))
            pulse (+ 0.05 (* 0.013 (mod i 6)))]
        (fill-block! pos i 2
                     (fn [f]
                       ;; Orbit position plus a small independent wobble, so no
                       ;; two neighbours trace the same path.
                       (let [a (+ ph (* f 0.014 (if (even? i) 1 -1)))]
                         [(+ (* ring (js/Math.cos a)) (* 5 (js/Math.sin (* f wob))))
                          (+ (* ring (js/Math.sin a)) (* 5 (js/Math.cos (+ 1.1 (* f wob)))))])))
        (fill-block! rot i 1 (fn [f] [(+ ph (* f spin))]))
        (fill-block! scale i 2
                     (fn [f]
                       (let [s (+ 1.0 (* 0.45 (js/Math.sin (+ ph (* f pulse)))))]
                         [s s])))
        ;; Every eleventh shape is absent for a window that moves with i.
        (when (zero? (mod i 11))
          (let [from (mod (* i 9) frames)
                to   (min frames (+ from 34))]
            (doseq [f (range from to)]
              (aset state (+ (* i frames) f) ch/absent-bit))))))
    {"swarm/pos"   {:data pos :state state}
     "swarm/rot"   {:data rot :state nil}
     "swarm/scale" {:data scale :state nil}}))

;; ---------------------------------------------------------------------------
;; the nodes

(defn- dense [store i stride]
  {:animated? true :interp :hold
   :dense {:store store :offset (* i frames stride) :stride stride :frames frames}
   ;; Provenance, which nothing in the renderer reads. Here it is honest about
   ;; where these numbers came from, the same way :roto/lips-outer will be.
   :generated {:by :demo/swarm}
   :over []})

(defn- orbit-node [i]
  {:id (keyword (str "orbit-" i)) :kind :group :parent :root
   :z (str "b" i)
   :channels {[:xform :pos] (dense "swarm/orbit-pos" i 2)
              [:xform :rot] (dense "swarm/orbit-rot" i 1)}})

(defn- shape-node [i]
  (let [orbit (keyword (str "orbit-" (mod i n-orbits)))
        tone  (nth tones (mod i (count tones)))
        kind  (case (mod i 7) 5 :disc 6 :rect :poly)
        verts (+ 3 (mod i 10))
        size  (+ 3.5 (* 0.9 (mod i 8)))
        base  {:id (keyword (str "s-" i)) :kind kind :parent orbit
               ;; Fractional index among siblings. Zero-padded so the strings
               ;; sort the way the numbers do — "c9" would otherwise land after
               ;; "c10", which is the classic way a z order goes subtly wrong.
               :z (str "c" (.padStart (str i) 4 "0"))
               :channels {[:xform :pos]   (dense "swarm/pos" i 2)
                          [:xform :rot]   (dense "swarm/rot" i 1)
                          [:xform :scale] (dense "swarm/scale" i 2)
                          [:style :color] (ch/framed tone)}}]
    (update base :channels merge
            (case kind
              :poly {[:geom :pts]    (ch/framed (regular-poly verts size (* 0.3 i)))}
              :disc {[:geom :radius] (ch/framed (* 0.75 size))}
              :rect {[:geom :size]   (ch/framed (js/Math.round size))}))))

(def store
  (delay (merge (orbit-blocks) (shape-blocks))))

(def clip
  (delay
    {:name   "swarm"
     :fps    fps
     :width  320
     :height 200
     :symbols
     {:main
      {:id :main
       :frames frames
       :nodes
       (into {:root {:id :root :kind :group :parent nil :z "a1"
                   ;; On 2s, like everything else. A hundred and twenty shapes
                   ;; cutting on one grid reads as animation; the same shapes on
                   ;; their own grids read as a screensaver, which is the whole
                   ;; argument for exposure inheriting strictly.
                   :time {:mode :map :expose 2}}}
             (concat (map (juxt :id identity) (map orbit-node (range n-orbits)))
                     (map (juxt :id identity) (map shape-node (range n-shapes)))))}}}))
